/**
 * L'envoi d'un bon de commande au fournisseur (V1.6-14.3).
 *
 * Ce que cet écran tient, et qui est tout le ticket :
 *
 * * **deux états cohabitent et ne se confondent jamais.** « Statut commande »
 *   dit où en est l'engagement commercial ; « Envoi fournisseur » dit ce qui
 *   est réellement sorti. Un bon peut être *Brouillon* avec un envoi *Échec*,
 *   et afficher l'un à la place de l'autre ferait croire qu'il est parti ;
 * * **l'écran ne décide d'aucun droit.** `can_send`, `can_retry` et le motif du
 *   refus viennent du serveur. Un bouton caché est une politesse, pas une
 *   frontière ;
 * * **une livraison incertaine n'offre rien.** C'est le seul cas où ne proposer
 *   aucune action est la bonne interface : on ne sait pas si le fournisseur a
 *   reçu, et renvoyer lui ferait peut-être expédier deux fois ;
 * * **rien n'est annoncé avant la réponse du serveur.** Pas de « envoyé »
 *   optimiste : l'état affiché est celui que le serveur vient de rendre.
 *
 * Le destinataire montré avant l'envoi est celui que le **serveur** a résolu,
 * jamais une adresse reconstruite ici depuis la fiche fournisseur : ce qui
 * partira doit être ce qu'on a lu.
 */
import { useState } from "react";
import { MailCheck, MailWarning, RefreshCw, Send } from "lucide-react";

import {
  useQueuePurchaseOrderSend,
  useRetryPurchaseOrderSend,
  usePurchaseOrderDispatch,
} from "@/api/purchase-orders";
import type { PlannedPurchaseOrderDetail, PurchaseOrderDispatch } from "@/api/types";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { formatAmount } from "@/lib/comparison-display";
import {
  formatDateTime,
  formatDispatchBlockedReason,
  formatPurchaseOrderDispatchState,
  formatSendError,
  pluralize,
} from "@/lib/format";
import { reportMutationError } from "@/lib/mutation-errors";
import { dispatchTone } from "@/lib/stock";

export function DispatchCard({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const { data, isLoading } = usePurchaseOrderDispatch(detail.order.id);

  return (
    <Card>
      <CardHeader title="Envoi fournisseur" />
      <CardBody className="space-y-3">
        {isLoading || !data ? (
          <p className="text-[13px] text-ink-subtle">Lecture de l'état d'envoi…</p>
        ) : (
          <DispatchBody detail={detail} dispatch={data} />
        )}
      </CardBody>
    </Card>
  );
}

function DispatchBody({
  detail,
  dispatch,
}: {
  detail: PlannedPurchaseOrderDetail;
  dispatch: PurchaseOrderDispatch;
}) {
  const state = dispatch.dispatch_state;
  const blocked = formatDispatchBlockedReason(dispatch.blocked_reason);

  return (
    <>
      {/* Le mot avant la couleur : un badge se lit aussi en noir et blanc. */}
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={dispatchTone(state)}>
          {formatPurchaseOrderDispatchState(state)}
        </StatusBadge>
        {dispatch.attempts > 0 && (
          <span className="text-[12px] text-ink-subtle">
            {pluralize(dispatch.attempts, "tentative", "tentatives")}
          </span>
        )}
      </div>

      {state === "UNKNOWN_DELIVERY" && <UncertainDelivery dispatch={dispatch} />}

      <dl className="grid gap-x-4 gap-y-1.5 text-[13px] sm:grid-cols-[auto_1fr]">
        {dispatch.recipient && (
          <Row label="Destinataire">{dispatch.recipient}</Row>
        )}
        {!dispatch.recipient && dispatch.preview_recipient && (
          <Row label="Destinataire prévu">{dispatch.preview_recipient}</Row>
        )}
        {dispatch.queued_at && (
          <Row label="Mis en file">{formatDateTime(dispatch.queued_at)}</Row>
        )}
        {dispatch.queued_by && <Row label="Demandé par">{dispatch.queued_by}</Row>}
        {dispatch.sent_at && <Row label="Envoyé le">{formatDateTime(dispatch.sent_at)}</Row>}
        {dispatch.failed_at && state !== "UNKNOWN_DELIVERY" && (
          <Row label="Dernière tentative">{formatDateTime(dispatch.failed_at)}</Row>
        )}
      </dl>

      {state === "FAILED" && dispatch.last_error && (
        <p className="rounded-md border border-status-out/30 bg-danger-subtle px-3 py-2 text-[13px] text-status-out">
          {formatSendError(dispatch.last_error)}
        </p>
      )}

      {state === "SENT" && (
        <p className="flex items-start gap-1.5 text-[12px] text-ink-subtle">
          <MailCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Le service d'envoi a accepté le message. Un accusé du fournisseur reste un autre fait.
        </p>
      )}

      {blocked && state !== "UNKNOWN_DELIVERY" && (
        <p className="text-[12px] text-ink-muted">{blocked}</p>
      )}

      <Actions detail={detail} dispatch={dispatch} />
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-ink-subtle">{label}</dt>
      <dd className="text-ink break-all">{children}</dd>
    </>
  );
}

/**
 * Le cas le plus délicat de l'écran.
 *
 * Distinct d'un échec, et dit en toutes lettres plutôt que par une couleur :
 * on ne sait pas si le fournisseur a reçu. Aucune action n'est proposée, parce
 * qu'aucune n'est sûre.
 */
function UncertainDelivery({ dispatch }: { dispatch: PurchaseOrderDispatch }) {
  return (
    <div
      className="rounded-md border border-warning/30 bg-warning-subtle px-3 py-2 text-[13px] text-warning"
      role="note"
    >
      <p className="flex items-start gap-1.5 font-medium">
        <MailWarning className="mt-0.5 size-4 shrink-0" aria-hidden />
        Livraison incertaine
      </p>
      <p className="mt-1">
        HD-Vault ne peut pas confirmer si le fournisseur a reçu ce bon. Vérifiez auprès de lui
        avant toute nouvelle action : renvoyer lui ferait peut-être expédier deux fois.
      </p>
      {dispatch.outbound_message_id && (
        <p className="mt-1 text-[11px] break-all opacity-80">
          Message {dispatch.outbound_message_id}
        </p>
      )}
    </div>
  );
}

function Actions({
  detail,
  dispatch,
}: {
  detail: PlannedPurchaseOrderDetail;
  dispatch: PurchaseOrderDispatch;
}) {
  const toast = useToast();
  const queue = useQueuePurchaseOrderSend();
  const retry = useRetryPurchaseOrderSend();
  const [confirming, setConfirming] = useState(false);

  const busy = queue.isPending || retry.isPending;

  async function send() {
    try {
      const result = await queue.mutateAsync({ orderId: detail.order.id });
      setConfirming(false);
      // Ce qu'on annonce est ce que le serveur vient de dire, jamais un
      // « envoyé » optimiste posé avant la réponse.
      if (result.dispatch_state === "SENT") {
        toast.success("Bon envoyé", `Transmis à ${result.recipient ?? "le fournisseur"}.`);
      } else {
        toast.info("Envoi enregistré", formatPurchaseOrderDispatchState(result.dispatch_state));
      }
    } catch (error) {
      reportMutationError(toast, "Bon non envoyé", error);
    }
  }

  async function again() {
    try {
      const result = await retry.mutateAsync({ orderId: detail.order.id });
      if (result.dispatch_state === "SENT") {
        toast.success("Bon envoyé", `Transmis à ${result.recipient ?? "le fournisseur"}.`);
      } else {
        toast.info("Nouvel essai", formatPurchaseOrderDispatchState(result.dispatch_state));
      }
    } catch (error) {
      reportMutationError(toast, "Envoi non repris", error);
    }
  }

  return (
    <>
      {dispatch.can_send && (
        <Button
          variant="primary"
          size="sm"
          fullWidth
          disabled={busy}
          onClick={() => setConfirming(true)}
        >
          <Send className="size-4" aria-hidden />
          Envoyer ce bon
        </Button>
      )}

      {dispatch.can_retry && (
        <Button variant="secondary" size="sm" fullWidth disabled={busy} onClick={() => void again()}>
          <RefreshCw className="size-4" aria-hidden />
          {retry.isPending ? "Nouvel essai…" : "Réessayer l'envoi"}
        </Button>
      )}

      <Dialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Envoyer ce bon au fournisseur ?"
        description="Cette action enverra le bon au fournisseur. Elle ne s'annule pas."
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
              Annuler
            </Button>
            <Button variant="primary" size="sm" disabled={busy} onClick={() => void send()}>
              {queue.isPending ? "Envoi…" : "Envoyer"}
            </Button>
          </>
        }
      >
        <dl className="grid gap-x-4 gap-y-1.5 text-[13px] sm:grid-cols-[auto_1fr]">
          <Row label="Bon">{detail.order.code}</Row>
          <Row label="Fournisseur">{detail.order.supplier_name}</Row>
          <Row label="Destinataire">
            {dispatch.preview_recipient ?? "Aucune adresse"}
          </Row>
          <Row label="Lignes">{pluralize(detail.order.line_count, "ligne", "lignes")}</Row>
          <Row label="Total">
            {formatAmount(detail.order.totals.total, detail.order.totals.currency) ?? "Inconnu"}
          </Row>
        </dl>
      </Dialog>
    </>
  );
}
